<template>
  <section
    class="version-history-panel"
    data-onboarding="history-panel"
    :style="panelStyle"
  >
    <header class="version-history-panel__header">
      <div class="version-history-panel__heading">
        <h2 class="version-history-panel__title">提交记录</h2>
      </div>
      <div class="version-history-panel__actions">
        <n-tooltip
          trigger="hover"
          placement="top"
          :show-arrow="false"
          :theme-overrides="tooltipThemeOverrides"
          :content-style="tooltipContentStyle"
        >
          <template #trigger>
            <n-button
              class="version-history-panel__refresh-button"
              size="tiny"
              circle
              quaternary
              aria-label="刷新提交记录"
              :aria-busy="loading"
              :loading="loading"
              :disabled="loading"
              @click="handleRefreshClick"
            >
              <template #icon>
                <n-icon>
                  <Renew />
                </n-icon>
              </template>
            </n-button>
          </template>
          <span>刷新提交记录</span>
        </n-tooltip>
        <n-tag
          v-if="readonlyPreview"
          size="small"
          type="warning"
          round
        >
          历史版本仅支持查看
        </n-tag>
      </div>
    </header>

    <n-spin class="version-history-panel__spin" :show="loading" size="small">
      <n-empty
        v-if="records.length === 0"
        class="version-history-panel__empty"
        description="暂无提交记录"
        size="small"
      />
      <div v-else class="version-history-panel__body">
        <VersionHistoryGraph
          class="version-history-panel__graph"
          :graph="graph"
          :row-ids="rowIds"
          :selected-commit-id="selectedCommitId"
          :row-height="VERSION_HISTORY_ROW_HEIGHT"
          :row-gap="VERSION_HISTORY_ROW_GAP"
          :row-content-offset-y="VERSION_HISTORY_DOT_TITLE_OFFSET"
        />
        <div class="version-history-panel__list">
          <n-popover
            v-for="record in records"
            :key="record.id"
            trigger="hover"
            placement="right-start"
            display-directive="show"
            :show="hoveredCommitId === record.id"
            :delay="0"
            :duration="0"
            :show-arrow="false"
          >
            <template #trigger>
              <div
            class="version-history-panel__item"
            :class="{
              'version-history-panel__item--current': isRecordCurrentEditing(record),
              'version-history-panel__item--selected': record.id === selectedCommitId,
              'version-history-panel__item--hovered': hoveredCommitId === record.id,
              'version-history-panel__item--switching': isSwitchingCommit(record.id),
            }"
            :style="recordStyle"
            @mouseenter="showCommitPopover(record.id)"
            @mouseleave="scheduleHideCommitPopover(record.id)"
          >
            <div
              class="version-history-panel__select-area"
              role="button"
              tabindex="0"
              :aria-pressed="record.id === selectedCommitId"
              :aria-busy="isSwitchingCommit(record.id)"
              :data-commit-id="record.id"
              @click="handleSelect(record.id)"
              @mouseenter="showCommitPopover(record.id)"
              @mouseleave="scheduleHideCommitPopover(record.id)"
              @focusin="showCommitPopover(record.id)"
              @focusout="scheduleHideCommitPopover(record.id)"
              @keydown.enter.prevent="handleSelect(record.id)"
              @keydown.space.prevent="handleSelect(record.id)"
            >
              <span class="version-history-panel__switching-slot" aria-hidden="true">
                <n-spin
                  v-if="isSwitchingCommit(record.id)"
                  class="version-history-panel__switching-spin"
                  size="small"
                />
              </span>
              <span
                class="version-history-panel__summary"
                :data-commit-summary-id="record.id"
              >
                <span
                  class="version-history-panel__summary-primary"
                  :data-commit-primary-id="record.id"
                >
                  <span class="version-history-panel__message">{{ record.message }}</span>
                  <span class="version-history-panel__summary-author">{{ record.authorName }}</span>
                </span>
                <span
                  class="version-history-panel__summary-meta"
                  :data-commit-meta-id="record.id"
                >
                  <span
                    class="version-history-panel__summary-time"
                    :data-commit-time-id="record.id"
                  >
                    {{ formatCommitTime(record.committedAt) }}
                  </span>
                  <span
                    v-if="isRecordCurrentEditing(record)"
                    class="version-history-panel__state-badge version-history-panel__state-badge--current"
                    :data-current-workspace-commit-id="record.id"
                  >
                    当前编辑
                  </span>
                  <span
                    v-if="isRecordPreviewing(record)"
                    class="version-history-panel__state-badge version-history-panel__state-badge--preview"
                  >
                    正在预览
                  </span>
                </span>
              </span>
            </div>
              </div>
            </template>

            <div
              class="version-history-panel__popover"
              :data-commit-popover-id="record.id"
              @mouseenter="showCommitPopover(record.id)"
              @mouseleave="scheduleHideCommitPopover(record.id)"
            >
              <p class="version-history-panel__popover-title">{{ record.message }}</p>
              <dl class="version-history-panel__popover-meta">
                <div>
                  <dt>分支</dt>
                  <dd>{{ record.branchName }}</dd>
                </div>
                <div>
                  <dt>负责人</dt>
                  <dd>{{ record.authorName }}</dd>
                </div>
                <div>
                  <dt>时间</dt>
                  <dd>{{ formatCommitTime(record.committedAt) }}</dd>
                </div>
              </dl>
              <div class="version-history-panel__popover-footer">
                <n-button
                  v-if="canContinueEdit(record)"
                  class="version-history-panel__popover-action"
                  size="small"
                  type="primary"
                  secondary
                  :loading="switching"
                  :disabled="switching"
                  :data-continue-edit-id="record.id"
                  @click.stop="handleContinueEdit(record.id)"
                >
                  <template #icon>
                    <n-icon>
                      <Edit />
                    </n-icon>
                  </template>
                  基于此版本继续编辑
                </n-button>
              </div>
            </div>
          </n-popover>
        </div>
      </div>
    </n-spin>
  </section>
</template>

<script setup lang="ts">
/**
 * 版本历史面板。
 *
 * 该组件仅接收外部状态并发出选择/继续编辑事件，避免把 Editor 的业务流程
 * 写入列表组件，后续接入侧栏或弹窗时可以复用同一视图。
 */
import { computed, onBeforeUnmount, ref, type CSSProperties } from 'vue';
import { NButton, NEmpty, NIcon, NPopover, NSpin, NTag, NTooltip } from 'naive-ui';
import { Edit, Renew } from '@vicons/carbon';
import VersionHistoryGraph from '@/components/editor/VersionHistoryGraph.vue';
import { useEditorTooltipTheme } from '@/hooks/useEditorTooltipTheme';
import { useGlobalConfigStore } from '@/stores';
import { buildVersionHistoryGraph, resolveBranchColors } from '@/services/version-workspace/graph/build-version-history-graph';
import type { ProjectCommitRecord } from '@/services/version-workspace';

const props = defineProps<{
  /** 历史提交记录，调用方负责排序。 */
  records: readonly ProjectCommitRecord[];
  /** 当前选中的提交 ID。 */
  selectedCommitId?: string | null;
  /** 是否正在加载提交记录。 */
  loading: boolean;
  /** 是否正在切换历史版本预览。 */
  switching: boolean;
  /** 当前正在切换模型的提交 ID。 */
  switchingCommitId: string | null;
  /** 是否处于只读历史预览状态。 */
  readonlyPreview: boolean;
}>();

const emit = defineEmits<{
  /** 选择历史提交。 */
  select: [commitId: string];
  /** 基于历史提交继续编辑。 */
  continueEdit: [commitId: string];
  /** 请求刷新当前项目的提交记录。 */
  refresh: [];
}>();

const hoveredCommitId = ref<string | null>(null);
let hidePopoverTimer: number | null = null;
const globalStore = useGlobalConfigStore();
const { tooltipThemeOverrides, tooltipContentStyle } = useEditorTooltipTheme({
  maxWidth: '180px',
});

/**
 * 历史图与列表共用的行高。
 *
 * 图形圆点和列表行中心必须使用同一个 pitch，否则记录变多后会出现累计错位。
 */
const VERSION_HISTORY_ROW_HEIGHT = 40;
/**
 * 历史列表行之间的轻量间距。
 *
 * 这个间距会参与 SVG 点位计算，避免列表越长时圆点与标题行产生累计错位。
 */
const VERSION_HISTORY_ROW_GAP = 2;
/**
 * 圆点相对单行顶部的标题对齐偏移。
 *
 * 提交记录是两行信息，圆点语义上对应提交标题，所以不能取整行中心。
 */
const VERSION_HISTORY_DOT_TITLE_OFFSET = 12;

/**
 * 面板根节点样式变量。
 *
 * 这里把列表行高暴露为 CSS 变量，并与传给图组件的数值共用同一常量，避免图/列表 pitch 双源维护。
 */
const panelStyle = computed<CSSProperties>(() => ({
  '--version-history-row-height': `${VERSION_HISTORY_ROW_HEIGHT}px`,
  '--version-history-row-gap': `${VERSION_HISTORY_ROW_GAP}px`,
}));

/**
 * 提交记录行样式。
 *
 * 行元素只引用面板根节点上的 CSS 变量，不直接写入数值，保证图和列表行高共用一个来源。
 */
const recordStyle = computed<CSSProperties>(() => ({
  height: 'var(--version-history-row-height)',
}));

/**
 * 构建历史分支图数据。
 *
 * 首色跟随当前主题主色，保证主线颜色与全站主题一致。
 *
 * @returns 与提交记录对应的分支图布局。
 */
const graph = computed(() => buildVersionHistoryGraph(
  props.records,
  resolveBranchColors(globalStore.mainColor.hex),
));

/**
 * 提取提交列表行顺序。
 *
 * @returns 提交 ID 列表。
 */
const rowIds = computed(() => props.records.map((record) => record.id));

/**
 * 判断提交记录是否是当前正在预览的历史版本。
 *
 * @param record 提交记录。
 * @returns 只读预览态且命中选中提交时返回 true。
 */
function isRecordPreviewing(record: ProjectCommitRecord): boolean {
  return props.readonlyPreview && record.id === props.selectedCommitId;
}

/**
 * 判断提交记录是否是当前编辑基准。
 *
 * 设计说明：只读预览态和编辑态在界面语义上互斥；预览中展示旧编辑基准会让用户误以为两个
 * 模型同时处于当前态，因此只在非预览态展示“当前编辑”。
 *
 * @param record 提交记录。
 * @returns 非只读预览态且记录是当前工作区提交时返回 true。
 */
function isRecordCurrentEditing(record: ProjectCommitRecord): boolean {
  return !props.readonlyPreview && record.isCurrentWorkspaceCommit;
}

/**
 * 判断提交是否允许展示“继续编辑”入口。
 *
 * 设计说明：编辑态下当前编辑提交已经是用户正在编辑的基准版本，再展示“基于此版本继续编辑”会让用户误以为
 * 还能从同一版本重复创建工作区，因此这里直接隐藏入口；只读预览态下该入口用于退出预览并回到编辑态，不能被当前基准标记遮蔽。
 *
 * @param record 提交记录。
 * @returns 当前视图语义允许继续编辑时返回 true。
 */
function canContinueEdit(record: ProjectCommitRecord): boolean {
  return props.readonlyPreview || !record.isCurrentWorkspaceCommit;
}

/**
 * 判断指定提交行是否正在执行模型加载。
 *
 * @param commitId 提交记录 ID。
 * @returns 该提交是当前切换目标时返回 true。
 */
function isSwitchingCommit(commitId: string): boolean {
  return props.switchingCommitId === commitId;
}

/**
 * 格式化提交时间。
 *
 * 这里优先使用 ISO 字符串自身携带的日期与分钟，避免测试环境和用户时区差异
 * 让同一提交显示成不同时间。
 *
 * @param committedAt ISO 格式提交时间。
 * @returns YYYY-MM-DD HH:mm 格式时间。
 */
function formatCommitTime(committedAt: string): string {
  const matched = committedAt.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/);
  if (matched) {
    return `${matched[1]} ${matched[2]}`;
  }

  const date = new Date(committedAt);
  if (Number.isNaN(date.getTime())) {
    return committedAt;
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hour = String(date.getHours()).padStart(2, '0');
  const minute = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day} ${hour}:${minute}`;
}

/**
 * 发出提交选择事件。
 *
 * @param commitId 被点击的提交 ID。
 */
function handleSelect(commitId: string): void {
  // 切换中仍允许选择另一条提交，由 controller 的 takeLatest 统一取消旧请求并加载最后一次选择。
  if (props.switching && isSwitchingCommit(commitId)) return;
  emit('select', commitId);
}

/**
 * 发出继续编辑事件。
 *
 * @param commitId 继续编辑的来源提交 ID。
 */
function handleContinueEdit(commitId: string): void {
  if (props.switching) return;
  emit('continueEdit', commitId);
}

/**
 * 发出刷新提交记录事件。
 */
function handleRefreshClick(): void {
  if (props.loading) {
    return;
  }

  emit('refresh');
}

/**
 * 清理延迟关闭弹框的定时器。
 */
function clearPopoverHideTimer(): void {
  if (hidePopoverTimer === null) return;
  window.clearTimeout(hidePopoverTimer);
  hidePopoverTimer = null;
}

/**
 * 显示指定提交的 hover 信息弹框。
 *
 * @param commitId 需要展示详情的提交 ID。
 */
function showCommitPopover(commitId: string): void {
  clearPopoverHideTimer();
  hoveredCommitId.value = commitId;
}

/**
 * 延迟收起指定提交的 hover 信息弹框。
 *
 * 这里保留一个很短的延迟，是为了允许鼠标从提交行移动到弹框内容区域时不中断操作按钮点击。
 *
 * @param commitId 需要尝试收起详情的提交 ID。
 */
function scheduleHideCommitPopover(commitId: string): void {
  clearPopoverHideTimer();
  hidePopoverTimer = window.setTimeout(() => {
    if (hoveredCommitId.value === commitId) {
      hoveredCommitId.value = null;
    }
    hidePopoverTimer = null;
  }, 120);
}

onBeforeUnmount(() => {
  clearPopoverHideTimer();
});
</script>

<style scoped>
.version-history-panel {
  --version-history-primary-text: var(--n-text-color);
  --version-history-secondary-text: color-mix(in srgb, var(--n-text-color) 58%, var(--n-color) 42%);
  --version-history-tertiary-text: color-mix(in srgb, var(--n-text-color) 42%, var(--n-color) 58%);
  --version-history-accent-color: var(--n-primary-color, var(--puppy-primary));
  --version-history-row-highlight-offset: -90px;
  --version-history-hover-bg: rgba(32, 128, 240, 0.16);
  --version-history-selected-bg: rgba(32, 128, 240, 0.24);
  --version-history-hover-bg: color-mix(in srgb, var(--version-history-accent-color) 20%, transparent);
  --version-history-selected-bg: color-mix(in srgb, var(--version-history-accent-color) 30%, transparent);
  --version-history-current-bg: color-mix(in srgb, var(--n-info-color, var(--puppy-primary)) 14%, transparent);
  --version-history-panel-bg: var(--editor-side-panel-drawer-bg, var(--n-color));
  --version-history-divider: var(
    --editor-side-panel-divider,
    color-mix(in srgb, var(--n-text-color) 14%, transparent)
  );
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
  background: var(--version-history-panel-bg);
  color: var(--version-history-primary-text);
}

.version-history-panel__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--editor-side-panel-header-gap, 10px);
  padding: var(--editor-side-panel-header-padding, 14px 14px 12px);
  border-bottom: 1px solid var(--version-history-divider);
  background: var(--version-history-panel-bg);
}

.version-history-panel__heading {
  min-width: 0;
  padding-top: 1px;
}

.version-history-panel__title {
  margin: 0;
  color: var(--n-text-color);
  font-size: var(--editor-side-panel-title-size, 15px);
  font-weight: var(--editor-side-panel-title-weight, 700);
  line-height: var(--editor-side-panel-title-line-height, 1.35);
}

.version-history-panel__actions {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: flex-end;
  gap: 7px;
}

.version-history-panel__refresh-button {
  color: var(--version-history-secondary-text);
}

.version-history-panel__refresh-button:hover,
.version-history-panel__refresh-button:focus-visible {
  color: var(--n-primary-color);
}

.version-history-panel__spin {
  flex: 1;
  min-height: 0;
}

.version-history-panel__spin :deep(.n-spin-content) {
  height: 100%;
  min-height: 0;
}

.version-history-panel__body {
  box-sizing: border-box;
  display: flex;
  align-items: flex-start;
  height: 100%;
  min-height: 0;
  overflow-y: auto;
  padding: 8px 12px 12px 10px;
}

.version-history-panel__graph {
  flex: 0 0 auto;
  margin-top: 0;
  margin-right: 8px;
  position: relative;
  z-index: 2;
}

.version-history-panel__list {
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
  gap: var(--version-history-row-gap);
  position: relative;
  z-index: 1;
}

.version-history-panel__item {
  --version-history-row-highlight-bg: transparent;

  box-sizing: border-box;
  display: flex;
  width: 100%;
  align-items: center;
  padding: 0;
  border: 1px solid transparent;
  background: transparent;
  color: var(--n-text-color);
  cursor: default;
  font: inherit;
  position: relative;
  text-align: left;
}

.version-history-panel__item::before {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: var(--version-history-row-highlight-offset);
  z-index: 0;
  border-radius: 0;
  content: '';
  pointer-events: none;
  background: var(--version-history-row-highlight-bg);
  transition: background-color 0.14s ease;
}

.version-history-panel__item:hover,
.version-history-panel__item--hovered,
.version-history-panel__item:focus-within {
  --version-history-row-highlight-bg: var(--version-history-hover-bg);
}

.version-history-panel__item--current {
  --version-history-row-highlight-bg: var(--version-history-current-bg);
}

.version-history-panel__select-area {
  display: flex;
  width: 100%;
  height: 100%;
  min-width: 0;
  align-items: center;
  gap: 6px;
  border-radius: 6px;
  cursor: pointer;
  padding: 5px 7px;
  position: relative;
  z-index: 1;
}

.version-history-panel__select-area:focus-visible {
  outline: 2px solid var(--version-history-accent-color);
  outline-offset: -2px;
}

.version-history-panel__item--selected {
  --version-history-row-highlight-bg: var(--version-history-selected-bg);

  border-color: transparent;
}

.version-history-panel__item--switching .version-history-panel__select-area {
  cursor: progress;
}

.version-history-panel__summary {
  display: flex;
  width: 100%;
  min-width: 0;
  flex: 1 1 auto;
  flex-direction: column;
  justify-content: center;
  gap: 3px;
}

.version-history-panel__switching-slot {
  display: inline-flex;
  flex: 0 0 16px;
  width: 16px;
  align-items: center;
  justify-content: center;
}

.version-history-panel__summary-primary {
  display: flex;
  width: 100%;
  min-width: 0;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

.version-history-panel__message {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  color: var(--version-history-primary-text);
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.version-history-panel__summary-author {
  display: inline-flex;
  flex: 0 0 auto;
  min-width: 0;
  align-items: center;
  color: var(--version-history-secondary-text);
  font-size: 12px;
  line-height: 1;
}

.version-history-panel__summary-author::before {
  content: "·";
  margin-right: 6px;
  color: var(--version-history-tertiary-text);
}

.version-history-panel__summary-meta {
  display: flex;
  width: 100%;
  min-width: 0;
  align-items: center;
  gap: 7px;
}

.version-history-panel__summary-time {
  display: block;
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  color: var(--version-history-secondary-text);
  font-size: 11px;
  line-height: 1.1;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.version-history-panel__state-badge {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  height: 18px;
  padding: 0 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  line-height: 18px;
  white-space: nowrap;
}

.version-history-panel__state-badge--current {
  background: color-mix(in srgb, var(--n-info-color, var(--puppy-primary)) 16%, transparent);
  color: var(--n-info-color, var(--puppy-primary));
}

.version-history-panel__state-badge--preview {
  background: color-mix(in srgb, var(--n-warning-color, #f0a020) 20%, transparent);
  color: var(--n-warning-color, #f0a020);
}

.version-history-panel__switching-spin {
  flex: 0 0 auto;
  width: 12px;
  height: 12px;
  font-size: 12px;
}

.version-history-panel__switching-spin :deep(.n-spin-body),
.version-history-panel__switching-spin :deep(.n-spin-content) {
  width: 12px;
  height: 12px;
}

.version-history-panel__popover {
  box-sizing: border-box;
  width: 320px;
  padding: 0;
  color: var(--n-text-color);
}

.version-history-panel__popover-title {
  margin: 0;
  color: var(--n-text-color);
  font-size: 13px;
  font-weight: 600;
  line-height: 1.3;
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.version-history-panel__popover-meta {
  display: grid;
  gap: 4px;
  margin: 9px 0 0;
  font-size: 12px;
  line-height: 1.3;
}

.version-history-panel__popover-meta div {
  display: grid;
  grid-template-columns: 48px minmax(0, 1fr);
  min-width: 0;
  column-gap: 8px;
}

.version-history-panel__popover-meta dt {
  color: var(--n-text-color-3);
  white-space: nowrap;
}

.version-history-panel__popover-meta dd {
  min-width: 0;
  margin: 0;
  color: var(--n-text-color);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  word-break: break-all;
}

.version-history-panel__popover-footer {
  display: flex;
  align-items: center;
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid color-mix(in srgb, var(--n-border-color) 62%, transparent);
}

.version-history-panel__popover-action {
  --n-height: 26px;
  --n-padding: 0 10px;
  max-width: 100%;
  min-width: 0;
  justify-content: flex-start;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.version-history-panel__popover-action:not(.n-button--disabled):hover {
  background: color-mix(in srgb, var(--n-primary-color-hover) 15%, transparent);
}

.version-history-panel__popover-action:not(.n-button--disabled) {
  background: color-mix(in srgb, var(--n-primary-color) 10%, transparent);
}

.version-history-panel__popover-action :deep(.n-button__border),
.version-history-panel__popover-action :deep(.n-button__state-border) {
  border-color: color-mix(in srgb, var(--n-primary-color) 46%, transparent);
}

.version-history-panel__popover-action :deep(.n-button__content) {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.version-history-panel__empty {
  padding: 48px 0;
}
</style>
